import { useContext, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ShepherdTourContext } from 'react-shepherd';
import { useSelector } from 'react-redux';
import Step from 'shepherd.js/src/types/step';
import Tour from 'shepherd.js/src/types/tour';
import { useFeatures } from './useFeature';
import { appContextSelector, isOneTimeOffsetEnabledSelector, isCorporateThemeSelector } from 'modules/app/appSlice';
import { currentUserSelector } from 'modules/user/userSlice';
import { CheckCircle } from 'components/Icons/Icons';
import { Flex, useTheme, Icon } from '@chakra-ui/react';

interface ShepherdButtonWithType extends Step.StepOptionsButton {
  type?: string;
}
interface ShepherdOptionsWithType extends Step.StepOptions {
  buttons?: ReadonlyArray<Step.StepOptionsButton | ShepherdButtonWithType>;
}

interface UseOnboarding {
  steps: Array<ShepherdOptionsWithType>;
  tour: Tour | null;
}

function beforeShow(element: string, redirectTo?: CallableFunction, additionalCondition = true) {
  return (): Promise<void> => {
    redirectTo && redirectTo();

    let attempts = 6;
    return new Promise(function (resolve) {
      const interval = setInterval(() => {
        if ((document.querySelector(element) && additionalCondition) || attempts <= 0) {
          clearInterval(interval);
          resolve();
        }
        attempts--;
      }, 250);
    });
  };
}

function resetTour(tour: Tour | null): void {
  if (tour) {
    tour.steps.map(step => step.destroy());
    tour.steps = [];
  }
}

const useShepherdConfirm = (): UseOnboarding => {
  const { t, ready } = useTranslation('offset');
  const tour = useContext(ShepherdTourContext);
  const { colors, radii } = useTheme();
  const history = useHistory();
  const features = useFeatures();
  const context = useSelector(appContextSelector);
  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const isOneTimeOffsetEnabled = useSelector(isOneTimeOffsetEnabledSelector);
  const currentUser = useSelector(currentUserSelector);
  const steps: ShepherdOptionsWithType[] = [];
  const CheckCircleString = renderToStaticMarkup(
    <Flex p="5px" bg={colors.primary[100]} borderRadius={radii.base} w="40px" h="40px" justify="center" align="center">
      <Icon as={CheckCircle} color={colors.primary[500]} />
    </Flex>,
  );

  useEffect(() => {
    if (ready && currentUser) {
      resetTour(tour);

      tour?.addStep({
        id: 'impact-module',
        attachTo: {
          element: '.impact-module',
          on: 'right-start',
        },
        beforeShowPromise: beforeShow('#impact-module'),
        modalOverlayOpeningPadding: 5,
        modalOverlayOpeningRadius: 9,
        buttons: [
          {
            classes: 'shepherd-button-secondary',
            text: t('orderClaim.viewHistory'),
            action: () => {
              tour?.complete();
              history.push(isCorporateTheme ? '/settings/orders' : '/orders');
            },
          },

          {
            classes: 'shepherd-button-primary',
            text: t('orderClaim.dismiss'),
            action: tour?.complete,
          },
        ],
        title: `<span class="onboarding_tile onboarding_tile--no-bg onboarding_tile--no-path-bg">${CheckCircleString}</span><span class="onboarding_title onboarding_title--trimmed">${t(
          'orderClaim.approveSuccessTitle',
        )}</span>`,
        text: t('orderClaim.approveSuccessDescription'),
      });
    }
  }, [ready, context, currentUser, features, isOneTimeOffsetEnabled]);

  return {
    steps,
    tour,
  };
};

export default useShepherdConfirm;
